import {
  Card,
  CbhIcon,
  FilePreviewTile,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { CardContent, DialogContent, Grid, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useFileUploaderContext } from "@src/appV2/redesign/Documents/useFileUploaderContext";
import { useEffect } from "react";

import { AddFileTile } from "../../../components/AddFileTile";
import { FileSelectionBottomSheet } from "../../../components/FileSelectionBottomSheet";
import { CONTENT_TYPES, FOOTER_BUTTON_MIN_WIDTH } from "../constants";

interface UploadStepProps {
  hasFiles: boolean;
  onNext: () => void;
  onBack: () => void;
  onFilePreview: (fileId: string) => void;
}

export function UploadStep(props: UploadStepProps) {
  const { hasFiles, onNext, onBack, onFilePreview } = props;
  const modalState = useModalState();
  const { uploadedFiles, addFile, removeFile, hasReachedMaxFilesCount } = useFileUploaderContext();
  const { showErrorToast } = useToast();

  // Auto-open file selection sheet on mount so users don't have to tap "Add file" manually
  useEffect(() => {
    if (uploadedFiles.length === 0) {
      modalState.openModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  return (
    <>
      <DialogContent>
        <Grid container spacing={5} columns={2}>
          {uploadedFiles.map((file) => (
            <Grid key={file.id} item xs={1}>
              <FilePreviewTile
                alt={`Document preview for ${file.id}`}
                fileUrl={file.dataUrlBase64}
                onPress={() => {
                  onFilePreview(file.id);
                }}
                onRemove={() => {
                  removeFile(file.id);
                }}
              />
            </Grid>
          ))}
          {!hasReachedMaxFilesCount && (
            <Grid item xs={1}>
              <AddFileTile
                onClick={() => {
                  modalState.openModal();
                }}
              />
            </Grid>
          )}
        </Grid>

        <FileSelectionBottomSheet
          modalState={modalState}
          contentTypes={CONTENT_TYPES}
          callout={
            <Card variant="quaternary" sx={{ paddingY: 1 }}>
              <CardContent>
                <Stack direction="row" gap={3} alignItems="flex-start">
                  <CbhIcon type="lightbulb-gray" size="medium" />
                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
                    Make sure your images or files are in high-quality and easy to read for
                    approval.
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          }
          onFileAdded={(file) => {
            addFile(file);
          }}
          onFilesAdded={(files) => {
            for (const file of files) {
              addFile(file);
            }
          }}
          onCameraPermissionDenied={() => {
            showErrorToast("Camera permission is required to take a photo.");
          }}
        />
      </DialogContent>

      <DialogFooter withShadow orientation="horizontal">
        <Button
          size="large"
          variant="text"
          sx={{ minWidth: FOOTER_BUTTON_MIN_WIDTH }}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          size="large"
          disabled={!hasFiles}
          variant="contained"
          sx={{ minWidth: FOOTER_BUTTON_MIN_WIDTH }}
          onClick={onNext}
        >
          Next
        </Button>
      </DialogFooter>
    </>
  );
}
